import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import SortHeader, { useSort, sortArr } from '../components/SortHeader';

const emptyForm = { code: '', nom: '', forme_juridique: '', capital: 0, adresse: '', siret: '', rcs: '', representant: '', email: '', telephone: '' };

export default function Promoteurs() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [sort, toggle] = useSort('code', 'asc');

  const load = async () => setList((await api.get('/promoteurs')).data);
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ ...p, capital: p.capital || 0 });
    setEditingId(p.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, capital: Number(form.capital) || 0, code: form.code.toUpperCase() };
      if (editingId) {
        await api.put(`/promoteurs/${editingId}`, payload);
        toast.success('Promoteur mis à jour');
      } else {
        await api.post('/promoteurs', payload);
        toast.success('Promoteur créé');
      }
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    await api.delete(`/promoteurs/${id}`); toast.success('Supprimé'); load();
  };

  return (
    <Layout title="Clients" subtitle="Maîtres d'ouvrage & entités"
      actions={
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button onClick={openCreate} className="rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="nouveau-promoteur-btn">
              <Plus size={16} className="mr-2" /> Nouveau promoteur
            </Button>
          </SheetTrigger>
          <SheetContent className="rounded-none w-full sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-display font-bold text-2xl tracking-tight">{editingId ? 'Modifier' : 'Nouveau promoteur'}</SheetTitle>
            </SheetHeader>
            <form onSubmit={submit} className="space-y-4 mt-6">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Code (2 lettres) *</Label>
                  <Input required maxLength={2} className="rounded-none font-mono uppercase" value={form.code}
                          onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="promo-code-input" />
                </div>
                <div className="col-span-2">
                  <Label>Nom *</Label>
                  <Input required className="rounded-none" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="promo-nom-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Forme juridique</Label>
                  <Input className="rounded-none" value={form.forme_juridique} onChange={(e) => setForm({ ...form, forme_juridique: e.target.value })} />
                </div>
                <div>
                  <Label>Capital (€)</Label>
                  <Input type="number" className="rounded-none font-mono" value={form.capital} onChange={(e) => setForm({ ...form, capital: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Adresse</Label>
                <Input className="rounded-none" value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>SIRET</Label>
                  <Input className="rounded-none font-mono" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} />
                </div>
                <div>
                  <Label>RCS</Label>
                  <Input className="rounded-none" value={form.rcs} onChange={(e) => setForm({ ...form, rcs: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Représentant</Label>
                <Input className="rounded-none" value={form.representant} onChange={(e) => setForm({ ...form, representant: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Email</Label>
                  <Input type="email" className="rounded-none" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input className="rounded-none font-mono" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />
                </div>
              </div>
              <Button type="submit" className="w-full rounded-none bg-[#002FA7] hover:bg-[#001B63]">{editingId ? 'Mettre à jour' : 'Créer'}</Button>
            </form>
          </SheetContent>
        </Sheet>
      }>
      <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="promoteurs-table">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-900">
            <tr className="text-left">
              <SortHeader sort={sort} onToggle={toggle} k="code" label="Code" />
              <SortHeader sort={sort} onToggle={toggle} k="nom" label="Nom" />
              <SortHeader sort={sort} onToggle={toggle} k="forme_juridique" label="Forme" />
              <SortHeader sort={sort} onToggle={toggle} k="representant" label="Représentant" />
              <SortHeader sort={sort} onToggle={toggle} k="email" label="Email" />
              <SortHeader sort={sort} onToggle={toggle} k="telephone" label="Tél." />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortArr(list, sort, (o, k) => o[k]).map((p) => (
              <tr key={p.id} className="border-b border-zinc-200 hover:bg-zinc-50">
                <td className="px-4 py-3 font-mono font-medium text-[#002FA7]">{p.code}</td>
                <td className="px-4 py-3 font-medium">{p.nom}</td>
                <td className="px-4 py-3 text-zinc-600 text-xs">{p.forme_juridique || '—'}</td>
                <td className="px-4 py-3">{p.representant || '—'}</td>
                <td className="px-4 py-3 font-mono text-xs">{p.email || '—'}</td>
                <td className="px-4 py-3 font-mono text-xs">{p.telephone || '—'}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => openEdit(p)} className="p-1.5 hover:bg-zinc-200"><Pencil size={13} /></button>
                    <button onClick={() => remove(p.id)} className="p-1.5 hover:bg-rose-100 text-rose-600"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
